import { Api } from 'api';
import { ChartData } from 'chart.js';
import { downloadBlobAsFile, getRandomColorForChartPie } from 'core';
import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { Pie } from 'react-chartjs-2';
import { useMutation, useQuery } from 'react-query';
import {
  AdminPackageSaleReportGraphData,
  ExpertisePoint,
  ReportingQueryCacheKey,
  PackageSalesReportDataResponse,
} from 'types';
import { Box, Button, Center, Flex, Icon, Text, useToast, useColorModeValue } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { hideLoading, showLoading } from 'features';
import { DownloadIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'components';

export interface AdminReportExpertisePackagesProps {
  beginDate?: Date;
  endDate?: Date;
  expertisePoint?: ExpertisePoint;
  groupType?: number;
}

type PackageSaleReportChartData = ChartData<'pie', number[], string>;
type PackageSaleReportQueryData = {
  isEmpty: boolean;
  graphData: PackageSaleReportChartData;
  tableData?: PackageSalesReportDataResponse[];
};

export const AdminReportExpertisePackages: React.FC<AdminReportExpertisePackagesProps> = ({
  beginDate,
  endDate,
  expertisePoint,
  groupType,
}) => {
  const authUserInfo = useSelector((state: RootState) => state.auth.authUserInfo);
  const toast = useToast();
  const dispatch = useDispatch();

  // Color values for the table
  const tableHeaderBg = useColorModeValue('gray.700', 'gray.600');
  const tableHeaderText = useColorModeValue('white', 'white');

  const { data: reportData, isLoading } = useQuery(
    [
      ReportingQueryCacheKey.GetPackageSalesReport,
      beginDate?.getTime(),
      endDate?.getTime(),
      expertisePoint?.id,
      groupType,
    ],
    async (): Promise<PackageSaleReportQueryData | null> => {
      if (beginDate && endDate) {
        const response =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.getSalesReportByExpertisePackageAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
                groupType,
              )
            : await Api.Reporting.getSalesReportByExpertisePackage(beginDate.getTime(), endDate.getTime());

        const tableDataResponse = await Api.Reporting.getSalesReportByExpertisePackageData(
          beginDate.getTime(),
          endDate.getTime(),
          expertisePoint?.id,
          groupType,
        );

        const packageSaleData = response.data;
        const graphData: AdminPackageSaleReportGraphData = {
          packageLabels: [],
          packageSales: [],
        };
        packageSaleData.forEach((packageSale) => {
          graphData.packageLabels.push(packageSale.expertisePackage.name);
          graphData.packageSales.push(packageSale.count);
        });
        return {
          isEmpty: packageSaleData.length === 0,
          graphData: {
            labels: graphData.packageLabels,
            datasets: [
              {
                label: 'Paket',
                data: graphData.packageSales,
                backgroundColor: getRandomColorForChartPie(packageSaleData.length),
              },
            ],
          } as PackageSaleReportChartData,
          tableData: tableDataResponse.data,
        };
      }
      return null;
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!(beginDate && endDate),
    },
  );

  const getExportPackagesReport = useMutation<any, any>(
    async () => {
      dispatch(showLoading());
      if (beginDate && endDate) {
        const response =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.exportSalesReportByExpertisePackageAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
              )
            : await Api.Reporting.exportSalesReportByExpertisePackage(beginDate.getTime(), endDate.getTime());

        return response;
      }
      return null;
    },
    {
      onSuccess(data) {
        if (data) {
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
          const fileName = `Paketler${formattedDate}.xlsx`;
          downloadBlobAsFile(data, fileName);
        } else {
          toast({
            status: 'info',
            title: 'Rapor Bulunamadı',
            isClosable: true,
          });
        }
      },
      onSettled() {
        dispatch(hideLoading());
      },
      onError() {
        toast({
          status: 'error',
          title: 'Rapor İndirilirken Hata Oluştu',
          isClosable: true,
        });
      },
    },
  );

  const columnHelper = createColumnHelper<PackageSalesReportDataResponse>();

  const columns = [
    columnHelper.accessor('expertisePointName', {
      header: 'Bayi Adı',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('packageGroupName', {
      header: 'Kampanya Adı',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('packageName', {
      header: 'Paket Adı',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('packageCount', {
      header: 'Paket Adeti',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
  ];

  if (!reportData || isLoading) {
    return null;
  }

  if (reportData.isEmpty) {
    return (
      <Center>
        <Text fontSize="2xl" mt={2} mb={6}>
          Seçtiğiniz Tarih Aralığında Veri Bulunmuyor!
        </Text>
      </Center>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <Button
        width="max-content"
        variant="solid"
        leftIcon={<Icon as={DownloadIcon} />}
        onClick={() => getExportPackagesReport.mutate()}
      >
        Excel İndir
      </Button>

      {reportData.tableData && reportData.tableData.length > 0 && (
        <Box mt={6} overflowX="auto">
          <DataTable
            data={reportData.tableData}
            columns={columns}
            usePagination
            tableProps={{
              variant: 'simple',
              colorScheme: 'blue',
              size: 'sm',
              sx: {
                'thead tr': {
                  backgroundColor: tableHeaderBg,
                },
                'thead th': {
                  color: tableHeaderText,
                },
              },
            }}
          />
        </Box>
      )}
      <Center>
        <Box width="50%">
          <Pie data={reportData.graphData} />
        </Box>
      </Center>
    </Flex>
  );
};
